export type AuthStatePayload = Record<string, unknown> & {
  flow?: "trial-signup";
  redirectTo?: string;
  resolvePostLogin?: boolean;
  freshAuthenticationRequested?: boolean;
  featureBranch?: string;
  tidPopup?: {
    requestId: string;
  };
  silentFrame?: {
    requestId: string;
  };
  /** Set when the redirect was triggered automatically (token lost from memory on refresh/deep
   * link) rather than an explicit sign-in click, so the callback page can show a plain spinner
   * instead of the "Completing sign-in…" screen. */
  silent?: boolean;
  /** Locale of the page that started the redirect. The redirect URI registered with Trimble is
   * always /en/callback, so this is the only way the locale survives the round trip. */
  locale?: string;
};

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(input: string): Uint8Array {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const padLen = (4 - (padded.length % 4)) % 4;
  const binary = atob(padded + "=".repeat(padLen));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

export function encodeState(payload: AuthStatePayload): string {
  const json = JSON.stringify(payload);
  return toBase64Url(new TextEncoder().encode(json));
}

export function decodeState<T extends AuthStatePayload = AuthStatePayload>(raw: string | null | undefined): T | null {
  if (!raw) return null;
  try {
    const bytes = fromBase64Url(raw);
    const json = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === "object") return parsed as T;
    return null;
  } catch {
    return null;
  }
}
